"use client";

import { useState, useEffect } from "react";
import Icon from "@/components/ui/AppIcon";

interface ContactFormProps {
  initialTopic?: string;
}

export default function ContactForm({ initialTopic }: ContactFormProps) {
  const [isHydrated, setIsHydrated] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    role: "",
    email: "",
    topic: initialTopic || "",
    message: "",
    erp: "",
    platform: "",
    scenario: "",
    preferredTime: "",
  });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Mock submission
    console.log("Form submitted:", formData);
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
  };

  if (!isHydrated) {
    return (
      <div className="glass-card rounded-2xl p-8 max-w-2xl mx-auto">
        <div className="animate-pulse space-y-4">
          <div className="h-10 bg-neutral-800 rounded" />
          <div className="h-10 bg-neutral-800 rounded" />
          <div className="h-10 bg-neutral-800 rounded" />
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="glass-card rounded-2xl p-8 max-w-2xl mx-auto space-y-6"
    >
      {/* Name */}
      <div>
        <label
          htmlFor="name"
          className="block text-sm font-semibold text-neutral-400 uppercase tracking-wide mb-2"
        >
          Name *
        </label>
        <input
          type="text"
          id="name"
          name="name"
          required
          value={formData.name}
          onChange={handleChange}
          className="w-full px-4 py-3 bg-neutral-800 border border-white/10 rounded-lg text-white focus:border-blue-500 focus:outline-none transition-colors"
        />
      </div>

      {/* Company */}
      <div>
        <label
          htmlFor="company"
          className="block text-sm font-semibold text-neutral-400 uppercase tracking-wide mb-2"
        >
          Company *
        </label>
        <input
          type="text"
          id="company"
          name="company"
          required
          value={formData.company}
          onChange={handleChange}
          className="w-full px-4 py-3 bg-neutral-800 border border-white/10 rounded-lg text-white focus:border-blue-500 focus:outline-none transition-colors"
        />
      </div>

      {/* Role */}
      <div>
        <label
          htmlFor="role"
          className="block text-sm font-semibold text-neutral-400 uppercase tracking-wide mb-2"
        >
          Role *
        </label>
        <select
          id="role"
          name="role"
          required
          value={formData.role}
          onChange={handleChange}
          className="w-full px-4 py-3 bg-neutral-800 border border-white/10 rounded-lg text-white focus:border-blue-500 focus:outline-none transition-colors"
        >
          <option value="">Select role</option>
          <option value="enterprise_architect">Enterprise Architect</option>
          <option value="engineering_manager">Engineering Manager</option>
          <option value="it_director">IT Director</option>
          <option value="cio">CIO</option>
          <option value="other">Other</option>
        </select>
      </div>

      {/* Email */}
      <div>
        <label
          htmlFor="email"
          className="block text-sm font-semibold text-neutral-400 uppercase tracking-wide mb-2"
        >
          Email *
        </label>
        <input
          type="email"
          id="email"
          name="email"
          required
          value={formData.email}
          onChange={handleChange}
          className="w-full px-4 py-3 bg-neutral-800 border border-white/10 rounded-lg text-white focus:border-blue-500 focus:outline-none transition-colors"
        />
      </div>

      {/* Topic */}
      <div>
        <label
          htmlFor="topic"
          className="block text-sm font-semibold text-neutral-400 uppercase tracking-wide mb-2"
        >
          Topic *
        </label>
        <select
          id="topic"
          name="topic"
          required
          value={formData.topic}
          onChange={handleChange}
          className="w-full px-4 py-3 bg-neutral-800 border border-white/10 rounded-lg text-white focus:border-blue-500 focus:outline-none transition-colors"
        >
          <option value="">Select topic</option>
          <option value="demo">Demo Request</option>
          <option value="assessment">Assessment</option>
          <option value="delivery">Delivery</option>
          <option value="stabilization">Stabilization</option>
          <option value="partner">Partner/Alliance</option>
          <option value="architect">Architect Network</option>
        </select>
      </div>

      {/* Conditional Demo Fields */}
      {formData.topic === "demo" && (
        <>
          <div>
            <label
              htmlFor="erp"
              className="block text-sm font-semibold text-neutral-400 uppercase tracking-wide mb-2"
            >
              ERP
            </label>
            <select
              id="erp"
              name="erp"
              value={formData.erp}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-neutral-800 border border-white/10 rounded-lg text-white focus:border-blue-500 focus:outline-none transition-colors"
            >
              <option value="">Select ERP</option>
              <option value="sap">SAP</option>
              <option value="oracle">Oracle</option>
              <option value="netsuite">NetSuite</option>
              <option value="workday">Workday</option>
              <option value="dynamics">Dynamics</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label
              htmlFor="platform"
              className="block text-sm font-semibold text-neutral-400 uppercase tracking-wide mb-2"
            >
              Integration Platform
            </label>
            <select
              id="platform"
              name="platform"
              value={formData.platform}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-neutral-800 border border-white/10 rounded-lg text-white focus:border-blue-500 focus:outline-none transition-colors"
            >
              <option value="">Select platform</option>
              <option value="sap_is">SAP Integration Suite</option>
              <option value="mulesoft">MuleSoft</option>
              <option value="boomi">Boomi</option>
              <option value="azure">Azure</option>
              <option value="aws">AWS</option>
              <option value="opensource">Open-source</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label
              htmlFor="scenario"
              className="block text-sm font-semibold text-neutral-400 uppercase tracking-wide mb-2"
            >
              Sample Scenario
            </label>
            <textarea
              id="scenario"
              name="scenario"
              rows={3}
              value={formData.scenario}
              onChange={handleChange}
              placeholder="e.g., Inbound purchase order from supplier to ERP"
              className="w-full px-4 py-3 bg-neutral-800 border border-white/10 rounded-lg text-white focus:border-blue-500 focus:outline-none transition-colors"
            />
          </div>

          <div>
            <label
              htmlFor="preferredTime"
              className="block text-sm font-semibold text-neutral-400 uppercase tracking-wide mb-2"
            >
              Preferred Time
            </label>
            <input
              type="text"
              id="preferredTime"
              name="preferredTime"
              value={formData.preferredTime}
              onChange={handleChange}
              placeholder="e.g., Next Tuesday 2pm EST"
              className="w-full px-4 py-3 bg-neutral-800 border border-white/10 rounded-lg text-white focus:border-blue-500 focus:outline-none transition-colors"
            />
          </div>
        </>
      )}

      {/* Message */}
      <div>
        <label
          htmlFor="message"
          className="block text-sm font-semibold text-neutral-400 uppercase tracking-wide mb-2"
        >
          Message *
        </label>
        <textarea
          id="message"
          name="message"
          required
          rows={6}
          value={formData.message}
          onChange={handleChange}
          placeholder="Tell us about your integration challenge..."
          className="w-full px-4 py-3 bg-neutral-800 border border-white/10 rounded-lg text-white focus:border-blue-500 focus:outline-none transition-colors"
        />
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        className="w-full px-8 py-4 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-full text-lg font-medium hover:scale-105 transition-transform shadow-xl flex items-center justify-center gap-2"
      >
        {submitted ? (
          <>
            <Icon name="CheckCircleIcon" size={24} variant="solid" />
            Message Sent!
          </>
        ) : (
          <>
            Send Message
            <Icon name="PaperAirplaneIcon" size={20} />
          </>
        )}
      </button>

      {submitted && (
        <p className="text-center text-emerald-400 text-sm">
          Thank you! We&apos;ll respond within 24 hours.
        </p>
      )}
    </form>
  );
}